import React from 'react';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background: #000000;
  padding: 80px 5% 60px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 800px;
    height: 400px;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 35, 0, 0.08) 0%,
      rgba(255, 35, 0, 0.03) 40%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px 40px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const CTASection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 80px;
  margin-bottom: 80px;
  border-bottom: none;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #FF2300;
    box-shadow: 
      0 0 20px rgba(255, 35, 0, 0.3),
      0 0 40px rgba(255, 35, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding-bottom: 60px;
    margin-bottom: 60px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const CTAHeadline = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CTAButton = styled.button`
  background: white;
  color: black;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const SitemapSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 60px;
  margin-bottom: 80px;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }
  
  @media (max-width: 500px) {
    display: none;
  }
`;

const SitemapColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SitemapTitle = styled.h4`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px 0;
  letter-spacing: 0;
`;

const SitemapList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SitemapItem = styled.li``;

const SitemapLink = styled.a`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    text-align: left;
  }
`;

const LogoText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.02em;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 24px;
  
  @media (max-width: 768px) {
    gap: 16px;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`;

const LegalLink = styled.a`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const Footer = ({ onGetStarted }) => {
  const sitemapData = {
    product: [
      { title: 'Features', href: '#features' },
      { title: 'Pricing', href: '#pricing' },
      { title: 'Changelog', href: '#changelog' },
      { title: 'Docs', href: '#docs' }
    ],
    resources: [
      { title: 'Blog', href: '#blog' },
      { title: 'Help Center', href: '#help' },
      { title: 'Community', href: '#community' },
      { title: 'Contact', href: '#contact' }
    ],
    company: [
      { title: 'About', href: '#about' },
      { title: 'Careers', href: '#careers' },
      { title: 'Press Kit', href: '#press' },
      { title: 'Partners', href: '#partners' }
    ],
    legal: [
      { title: 'Privacy', href: '#privacy' },
      { title: 'Terms', href: '#terms' },
      { title: 'Security', href: '#security' },
      { title: 'Status', href: '#status' }
    ]
  };

  return (
    <FooterContainer>
      <FooterContent>
        <CTASection>
          <CTAHeadline>Get DIZEE</CTAHeadline>
          <CTAButton onClick={onGetStarted}>
            Get Started
          </CTAButton>
        </CTASection>

        <SitemapSection>
          <SitemapColumn>
            <SitemapTitle>Product</SitemapTitle>
            <SitemapList>
              {sitemapData.product.map((link, index) => (
                <SitemapItem key={index}>
                  <SitemapLink href={link.href}>{link.title}</SitemapLink>
                </SitemapItem>
              ))}
            </SitemapList>
          </SitemapColumn>

          <SitemapColumn>
            <SitemapTitle>Resources</SitemapTitle>
            <SitemapList>
              {sitemapData.resources.map((link, index) => (
                <SitemapItem key={index}>
                  <SitemapLink href={link.href}>{link.title}</SitemapLink>
                </SitemapItem>
              ))}
            </SitemapList>
          </SitemapColumn>

          <SitemapColumn>
            <SitemapTitle>Company</SitemapTitle>
            <SitemapList>
              {sitemapData.company.map((link, index) => (
                <SitemapItem key={index}>
                  <SitemapLink href={link.href}>{link.title}</SitemapLink>
                </SitemapItem>
              ))}
            </SitemapList>
          </SitemapColumn>

          <SitemapColumn>
            <SitemapTitle>Legal</SitemapTitle>
            <SitemapList>
              {sitemapData.legal.map((link, index) => (
                <SitemapItem key={index}>
                  <SitemapLink href={link.href}>{link.title}</SitemapLink>
                </SitemapItem>
              ))}
            </SitemapList>
          </SitemapColumn>
        </SitemapSection>

        <FooterBottom>
          <LogoText>DIZEE</LogoText>
          
          <LegalLinks>
            <LegalLink href="#privacy">Privacy Policy</LegalLink>
            <LegalLink href="#terms">Terms of Service</LegalLink>
          </LegalLinks>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 